// File Location: src/hooks/useNavigationGuard.js

import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getDashboardRouteByRole } from '../utils/navigationUtils';

export const useNavigationGuard = () => {
  const { currentUser, userRole, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading || !currentUser || !userRole) return;

    const publicRoutes = ['/', '/login', '/hospital-registration', '/lead-registration'];
    const dashboardRoute = getDashboardRouteByRole(userRole);

    // If authenticated user is on a public route, redirect to dashboard
    if (publicRoutes.includes(location.pathname)) {
      navigate(dashboardRoute, { replace: true });
      return;
    }

    // Handle browser back/forward navigation
    const handleNavigation = (event) => {
      const targetPath = window.location.pathname;
      
      // Prevent going back to public routes when authenticated
      if (publicRoutes.includes(targetPath)) {
        event.preventDefault();
        navigate(dashboardRoute, { replace: true });
      }
    };

    // Override browser navigation for authenticated users
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function(state, title, url) {
      const path = typeof url === 'string' ? url : url?.pathname;
      if (path && publicRoutes.includes(path)) {
        // Redirect to dashboard instead
        originalPushState.call(this, state, title, dashboardRoute);
      } else {
        originalPushState.call(this, state, title, url);
      }
    };

    window.history.replaceState = function(state, title, url) {
      const path = typeof url === 'string' ? url : url?.pathname;
      if (path && publicRoutes.includes(path)) {
        // Redirect to dashboard instead
        originalReplaceState.call(this, state, title, dashboardRoute);
      } else {
        originalReplaceState.call(this, state, title, url);
      }
    };

    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', handleNavigation);

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handleNavigation);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [currentUser, userRole, loading, navigate, location.pathname]);

  return {
    isGuarded: !loading && currentUser && userRole,
    dashboardRoute: userRole ? getDashboardRouteByRole(userRole) : null
  };
};

export default useNavigationGuard;
